import { Injectable, ElementRef } from '@angular/core';
import { AnimeParams } from 'animejs';

import { isTransformAnimation, isOpacityAnimation, isActionAnimation, isActionScroll } from 'shared/animations/animations-utils';
import {
  Animations, IdtAnimation, AnimationTransform, AnimationOpacity, AnimationScroll
} from 'shared/animations/animations.model';


@Injectable()
export class AnimationsRenderService {

  private anime!: typeof import('animejs');
  private readonly scrollAnimationState: { usedAnimations: string[] } = { usedAnimations: [] };

  private animeInstance: any;

  public async setUp(el: ElementRef, animations: Animations): Promise<() => void> {
    const enableAnime = Object.values(animations).find((animation) => {
      return isActionAnimation(animation) || isActionScroll(animation);
    });

    if (!this.anime && enableAnime) {
      this.anime = (await import('animejs')).default;
    }

    const events = Object.values(animations).map((animation) => {
      return this.setUpTrigger(el, animation);
    });

    return function destroy() {
      events.forEach((f) => f());
    };
  }

  public setUpTrigger(el: ElementRef, animation: IdtAnimation): () => void {
    const nativeElement = el.nativeElement as HTMLElement;

    let listener: () => void;
    if (isActionScroll(animation)) {
      listener = () => {
        this.scrollEvent(nativeElement, animation);
      };

      const loadListener = () => {
        listener();
        window.addEventListener('scroll', listener);
        window.removeEventListener('load', loadListener);
      };

      if (document.readyState === 'complete') {
        loadListener();
      } else {
        window.addEventListener('load', loadListener);
      }

      return () => {
        window.removeEventListener('scroll', listener);
      };
    }

    listener = () => {
      this.event(nativeElement, animation);
    };

    nativeElement.addEventListener(animation.trigger, listener);

    return () => {
      nativeElement.removeEventListener(animation.trigger, listener);
    };
  }

  public scrollEvent(el: HTMLElement, animation: AnimationScroll) {
    const elClientRect = el.getBoundingClientRect();
    const visibleSize = Math.floor(((window.innerHeight - elClientRect.top) * 100) / elClientRect.height);

    const animationsToApply = Object.entries(animation.scroll)
      .filter(([key, value]) => {
        return (
          visibleSize >= value.percentage
        && !this.scrollAnimationState.usedAnimations.includes(key)
        );
      });

    this.scrollAnimationState.usedAnimations.push(...animationsToApply.map(([key]) => key));

    animationsToApply.forEach(([, value]) => this.event(el, value.action));
  }

  public event(el: HTMLElement, animation: IdtAnimation) {
    if (isActionAnimation(animation)) {
      if (isTransformAnimation(animation)) {
        this.transformEvent(el, animation);
      } else if (isOpacityAnimation(animation)) {
        this.opacityEvent(el, animation);
      }
    }
  }

  private transformEvent(el: HTMLElement, animation: AnimationTransform) {
    const action = animation.action;
    const options: AnimeParams = {
      targets: el,
      easing: action.timing,
      // https://github.com/juliangarnier/anime/issues/730 there is a bug with duration 0 and reset() method
      duration: action.duration === 0 && action.canStopAnimation ? 1 : action.duration,
      delay: action.delay,
    };

    if (action.move) {
      if (isDefined(action.move.y) || isDefined(action.move.x)) {
        const x = action.move.x || '0px';
        const y = action.move.y || '0px';

        options.translateX = x;
        options.translateY = y;
      }
    }

    if (action.scale) {
      if (isDefined(action.scale.y) || isDefined(action.scale.x)) {
        const x = action.scale.x || '0px';
        const y = action.scale.y || '0px';

        options.scaleX = x;
        options.scaleY = y;
      }
    }

    if (action.rotate) {
      if (isDefined(action.rotate.value)) {
        const value = action.rotate.value;
        options.rotate = value;
      }
    }

    this.animeInstance = this.anime(options);

    if (action.canStopAnimation) {
      const stopAnimation = () => {
        this.animeInstance.reset();
        el.removeEventListener('mouseleave', stopAnimation);
      };
      el.addEventListener('mouseleave', stopAnimation);
    }
  }

  private opacityEvent(el: HTMLElement, animation: AnimationOpacity) {
    const action = animation.action;

    const options: AnimeParams = {
      targets: el,
      easing: action.timing,
      // https://github.com/juliangarnier/anime/issues/730 there is a bug with duration 0 and reset() method
      duration: action.duration === 0 && action.canStopAnimation ? 1 : action.duration,
      delay: action.delay,
      opacity: action.opacity
    };

    this.animeInstance = this.anime(options);

    if (action.canStopAnimation) {
      const stopAnimation = () => {
        this.animeInstance.reset();
        el.removeEventListener('mouseleave', stopAnimation);
      };
      el.addEventListener('mouseleave', stopAnimation);
    }
  }

}
